<script setup lang="ts">
import { Link, usePage } from '@inertiajs/vue3';
import {
    ArrowRight,
    CalendarClock,
    ChartSpline,
    FileText,
    LayoutDashboard,
    Plug,
    Plus,
    Scissors,
} from 'lucide-vue-next';
import { computed } from 'vue';
import AppLogo from '@/components/AppLogo.vue';
import CreditMeter from '@/components/CreditMeter.vue';
import NavMain from '@/components/NavMain.vue';
import NavUser from '@/components/NavUser.vue';
import TeamSwitcher from '@/components/TeamSwitcher.vue';
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarSeparator,
} from '@/components/ui/sidebar';
import { useTeamRoutes } from '@/composables/useTeamRoutes';
import { useCurrentUrl } from '@/composables/useCurrentUrl';
import { index as billingIndex } from '@/routes/billing';
import type { NavItem } from '@/types';

const team = useTeamRoutes();
const page = usePage();
const { isCurrentUrl, isCurrentOrParentUrl } = useCurrentUrl();
const createPostHref = computed(() => team.postsCreate());
const createPostActive = computed(() => isCurrentUrl(createPostHref.value));
const dashboardHref = computed(() => team.dashboard());

const onTrial = computed(() => Boolean(page.props.onTrial));
const subscribed = computed(() => Boolean(page.props.subscribed));
const trialDaysRemaining = computed(() => {
    const days = page.props.trialDaysRemaining;
    return typeof days === 'number' ? days : null;
});

const trialLabel = computed(() => {
    const days = trialDaysRemaining.value;
    if (days === null) return 'Trial';
    if (days <= 0) return 'Trial ends today';
    if (days === 1) return 'Trial · 1 day';
    return `Trial · ${days} days`;
});

// AI Creative Studio is hidden for now (routes still exist) — re-add its
// nav item here when it's ready to launch.
const createItems = computed<NavItem[]>(() => [
    { title: 'Posts', href: team.posts(), icon: FileText },
    { title: 'Content Studio', href: team.content(), icon: Scissors },
]);

// Organic / owned-audience work — scheduling, connected accounts, the
// "publish as myself" surface area. Kept distinct from paid/marketing so
// the mental model stays clean when the nav grows (inbox, DMs, etc. land here).
const socialItems = computed<NavItem[]>(() => [
    { title: 'Calendar', href: team.scheduler(), icon: CalendarClock },
    { title: 'Connections', href: team.connections(), icon: Plug },
]);

// The Marketing section (Ad Accounts / Campaigns / Creatives / Audiences)
// and AI Insights are hidden for now — routes still exist. Re-add nav items
// here when they're ready to launch:
//   Marketing: { title: 'Ad Accounts', href: team.adAccounts(), icon: CreditCard },
//              { title: 'Campaigns', href: team.ads(), icon: Megaphone },
//              { title: 'Creatives', href: team.adsCreatives(), icon: Images },
//              { title: 'Audiences', href: team.adsAudiences(), icon: Target },
//   Grow:      { title: 'AI Insights', href: team.insights(), icon: Sparkles },

// Measurement — where a team goes after publishing to ask "did it work?".
const growItems = computed<NavItem[]>(() => [
    { title: 'Analytics', href: team.analytics(), icon: ChartSpline },
]);
</script>

<template>
    <Sidebar collapsible="icon" variant="sidebar">
        <SidebarHeader>
            <SidebarMenu>
                <SidebarMenuItem>
                    <SidebarMenuButton size="lg" as-child>
                        <Link :href="team.dashboard()">
                            <AppLogo />
                        </Link>
                    </SidebarMenuButton>
                </SidebarMenuItem>
            </SidebarMenu>

            <!-- Team switcher — subtle surface of its own. -->
            <div class="rounded-xl border border-sidebar-border/70 bg-sidebar-accent/50 group-data-[collapsible=icon]:border-0 group-data-[collapsible=icon]:bg-transparent">
                <SidebarMenu>
                    <SidebarMenuItem>
                        <TeamSwitcher />
                    </SidebarMenuItem>
                </SidebarMenu>
            </div>

            <div class="px-0 group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:justify-center">
                <Link
                    :href="createPostHref"
                    class="flex w-full items-center justify-start gap-2 rounded-xl bg-gradient-to-br from-violet-500 to-cyan-500 px-3 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-95 group-data-[collapsible=icon]:size-8 group-data-[collapsible=icon]:w-8 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:p-0"
                    :class="createPostActive ? 'ring-2 ring-violet-300/40 ring-offset-2 ring-offset-sidebar' : ''"
                    title="Create Post"
                >
                    <Plus class="size-4 shrink-0" />
                    <span class="group-data-[collapsible=icon]:hidden">Create Post</span>
                </Link>
            </div>
        </SidebarHeader>

        <SidebarContent>
            <SidebarGroup class="px-2 py-0">
                <SidebarMenu>
                    <SidebarMenuItem>
                        <SidebarMenuButton
                            as-child
                            :is-active="isCurrentOrParentUrl(dashboardHref)"
                            tooltip="Dashboard"
                        >
                            <Link :href="dashboardHref">
                                <LayoutDashboard />
                                <span>Dashboard</span>
                            </Link>
                        </SidebarMenuButton>
                    </SidebarMenuItem>
                </SidebarMenu>
            </SidebarGroup>

            <SidebarSeparator class="my-2 opacity-60" />
            <NavMain label="Content" :items="createItems" />
            <SidebarSeparator class="my-2 opacity-60" />
            <NavMain label="Social" :items="socialItems" />
            <SidebarSeparator class="my-2 opacity-60" />
            <NavMain label="Grow" :items="growItems" />
        </SidebarContent>

        <SidebarFooter>
            <!-- Trial CTA — visible while exploring before subscribe -->
            <div
                v-if="onTrial && !subscribed"
                class="rounded-xl border border-violet-500/30 bg-gradient-to-br from-violet-500/15 via-fuchsia-500/10 to-cyan-500/10 p-2.5 group-data-[collapsible=icon]:hidden"
            >
                <div class="flex items-center justify-between gap-2 px-0.5">
                    <div class="min-w-0">
                        <div class="truncate text-xs font-semibold text-sidebar-foreground">
                            {{ trialLabel }}
                        </div>
                        <div class="truncate text-[10px] text-sidebar-foreground/60">
                            AI unlocks after upgrade
                        </div>
                    </div>
                    <Link
                        :href="billingIndex().url"
                        class="inline-flex flex-none items-center gap-1 rounded-lg bg-gradient-to-br from-violet-500 to-cyan-500 px-2.5 py-1.5 text-[11px] font-semibold text-white shadow-sm transition hover:opacity-95"
                    >
                        Upgrade
                        <ArrowRight class="size-3" />
                    </Link>
                </div>
            </div>

            <!-- Trial ended — nudge to subscribe -->
            <div
                v-else-if="!subscribed && !onTrial"
                class="rounded-xl border border-amber-500/30 bg-amber-500/10 p-2.5 group-data-[collapsible=icon]:hidden"
            >
                <div class="flex items-center justify-between gap-2 px-0.5">
                    <div class="min-w-0">
                        <div class="truncate text-xs font-semibold text-sidebar-foreground">
                            Trial ended
                        </div>
                        <div class="truncate text-[10px] text-sidebar-foreground/60">
                            Subscribe to continue
                        </div>
                    </div>
                    <Link
                        :href="billingIndex().url"
                        class="inline-flex flex-none items-center gap-1 rounded-lg bg-foreground px-2.5 py-1.5 text-[11px] font-semibold text-background transition hover:opacity-90"
                    >
                        Upgrade
                        <ArrowRight class="size-3" />
                    </Link>
                </div>
            </div>

            <!-- Credits — always in the bottom-left footer -->
            <div
                class="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-sidebar-accent/40 p-2 group-data-[collapsible=icon]:hidden"
            >
                <div class="min-w-0 flex-1 pl-1 text-xs text-sidebar-foreground/70">
                    Available credits
                </div>
                <CreditMeter compact />
            </div>

            <NavUser />
        </SidebarFooter>
    </Sidebar>
    <slot />
</template>
